import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import Dropdown from '@SonarCX/components/atoms/Dropdown'

import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectCategory } from '@SonarCX/slices/category'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'
import useTaskInputOpen from '@SonarCX/hooks/useTaskInputOpen'
import useStyles from './styles'

const getCategoryInfo = (categories: any[], id: string) => {
  if (!id) {
    return { name: 'No Category', isNone: true }
  }

  const category = categories.find(cat => cat.id === id)
  if (category) {
    return { name: category.name }
  }

  return { name: 'Private Category', isPrivate: true }
}

type CategoryProps = {
  task: LocalTask & { table?: any }
}

const Category: React.FC<CategoryProps> = ({ task }) => {
  const dispatch = useAppDispatch()

  const { categoriesValues: categories } = useAppSelector(selectCategory)

  const { updateTask } = useTaskAPI()
  const { canEdit } = useTaskPermissions(task)
  const { isOpen, handleClick, handleClose } = useTaskInputOpen(
    task,
    'CATEGORY'
  )

  const [startingValue, setStartingValue] = React.useState(task.category_id)
  const [value, setValue] = React.useState(startingValue)

  const { name, isNone, isPrivate } = React.useMemo(() => {
    return getCategoryInfo(categories, value)
  }, [categories, value])

  const classes = useStyles({ canEdit, isNone, isPrivate })

  React.useEffect(() => {
    setStartingValue(task.category_id)
    setValue(task.category_id)
  }, [task.category_id])

  const handleSelect = React.useCallback(
    value => {
      handleClose()
      if (startingValue !== value) {
        try {
          const newTask = { ...task, category_id: value }
          updateTask(newTask.id, newTask)
          setValue(value)
          dispatch(AddSuccessAlert('Changes saved'))
        } catch (err) {
          dispatch(AddErrorAlert({ err }))
        }
      }
    },
    [handleClose, startingValue, task, updateTask, dispatch]
  )

  const getClass = React.useCallback(
    ({ isNone, isPrivate }: { isNone?: boolean; isPrivate?: boolean }) => {
      return isPrivate ? classes.privateCat : isNone ? classes.noneCat : ''
    },
    [classes.privateCat, classes.noneCat]
  )

  return (
    <div className={classes.root}>
      {isOpen && canEdit ? (
        <Dropdown
          options={categories.map(cat => cat.id)}
          renderOption={id => getCategoryInfo(categories, id).name}
          selected={value}
          onSelect={handleSelect}
          onClose={handleClose}
          hideAdd
          stringOnly
          forceOpen
        >
          <div className={clsx(getClass({ isNone, isPrivate }))}>{name}</div>
        </Dropdown>
      ) : (
        <div
          onClick={handleClick}
          className={clsx(getClass({ isNone, isPrivate }))}
        >
          {name}
        </div>
      )}
    </div>
  )
}

export default React.memo(Category)
